<script setup lang="ts">
import { legalUpdatedAt } from '~/content/legal'

const props = defineProps<{
    title: string
    description: string
}>()

useSeoMeta({
    title: `${props.title} | Deboosere Chauffage Sanitaire`,
    description: props.description,
    ogTitle: `${props.title} | Deboosere Chauffage Sanitaire`,
    ogDescription: props.description,
    ogType: 'website',
})

useSchemaOrg([
    defineWebPage({
        name: props.title,
        description: props.description,
    }),
])
</script>

<template>
    <section class="bg-white pt-32 pb-20 lg:pt-44 lg:pb-28">
        <div class="container mx-auto px-4">
            <div class="mx-auto max-w-3xl">
                <NuxtLink to="/"
                    class="mb-10 inline-flex items-center gap-2 text-sm font-semibold text-slate-500 transition hover:text-sky-700">
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                        stroke-width="1.8" class="h-4 w-4">
                        <path d="m15 18-6-6 6-6" />
                    </svg>
                    Retour à l’accueil
                </NuxtLink>

                <p class="text-sm font-semibold uppercase tracking-[0.2em] text-sky-700">
                    Informations légales
                </p>

                <h1 class="mt-4 text-4xl font-black leading-tight text-slate-950 lg:text-5xl">
                    {{ title }}
                </h1>

                <p class="mt-4 text-sm text-slate-500">
                    Dernière mise à jour : {{ legalUpdatedAt }}
                </p>

                <div class="legal-content mt-12">
                    <slot />
                </div>
            </div>
        </div>
    </section>
</template>

<style scoped>
.legal-content {
    color: rgb(51 65 85);
    line-height: 1.75;
}

.legal-content :deep(h2) {
    margin-top: 2.5rem;
    margin-bottom: 1rem;
    font-size: 1.5rem;
    font-weight: 700;
    line-height: 1.3;
    color: rgb(2 6 23);
}

.legal-content :deep(h3) {
    margin-top: 1.75rem;
    margin-bottom: 0.75rem;
    font-size: 1.125rem;
    font-weight: 600;
    color: rgb(15 23 42);
}

.legal-content :deep(p),
.legal-content :deep(ul),
.legal-content :deep(ol) {
    margin-bottom: 1rem;
}

.legal-content :deep(ul) {
    list-style: disc;
    padding-left: 1.5rem;
}

.legal-content :deep(ol) {
    list-style: decimal;
    padding-left: 1.5rem;
}

.legal-content :deep(li) {
    margin-bottom: 0.375rem;
}

.legal-content :deep(a) {
    font-weight: 500;
    color: rgb(3 105 161);
    text-decoration: underline;
    text-underline-offset: 2px;
    overflow-wrap: anywhere;
}

.legal-content :deep(strong) {
    color: rgb(15 23 42);
}

.legal-content :deep(.legal-box) {
    margin: 1.5rem 0;
    border: 1px solid rgb(226 232 240);
    background: rgb(248 250 252);
    padding: 1.25rem 1.5rem;
}
</style>
